/**
 * Import pipeline often stores Markdown syntax as plain text inside Tiptap `paragraph`
 * nodes (see backend `_plain_text_to_tiptap_doc`). Detect that shape and parse into real
 * Tiptap JSON via markdown so editors and players show formatting instead of literal **…**.
 */

import {
    isTiptapDocument,
    markdownToTiptap,
    type TiptapJSON,
} from '@ai-content-hub/content-kit';
import { looksLikeMarkdown } from './detect';

function paragraphPlainTextAndUnmarked(p: TiptapJSON): string | null {
    const inner = p.content;
    if (inner == null || inner.length === 0) {
        return '';
    }
    let out = '';
    for (const c of inner) {
        if (c.type === 'hardBreak') {
            out += '\n';
            continue;
        }
        if (c.type !== 'text') {
            return null;
        }
        const marks = (c as { marks?: unknown[] }).marks;
        if (marks != null && marks.length > 0) {
            return null;
        }
        out += (c as { text?: string }).text ?? '';
    }
    return out;
}

function isPlainParagraphMarkdownCarrierDoc(doc: TiptapJSON): boolean {
    const nodes = doc.content ?? [];
    if (nodes.length === 0) {
        return false;
    }
    for (const n of nodes) {
        if (n.type !== 'paragraph') {
            return false;
        }
        if (paragraphPlainTextAndUnmarked(n) === null) {
            return false;
        }
    }
    return true;
}

function docToJoinedPlainLines(doc: TiptapJSON): string {
    const lines: string[] = [];
    for (const n of doc.content ?? []) {
        lines.push(paragraphPlainTextAndUnmarked(n) ?? '');
    }
    return lines.join('\n');
}

/** Markdown tables / pipe rows not covered by looksLikeMarkdown alone */
function plainTextLooksLikeMarkdownSource(text: string): boolean {
    const t = text.trim();
    if (!t) {
        return false;
    }
    if (looksLikeMarkdown(text)) {
        return true;
    }
    return /^\s*\|[^|\n]+\|\s*$/m.test(text);
}

/**
 * When content is a Tiptap doc whose paragraphs are unmarked plain text that still
 * contains Markdown markers, parse Markdown into structured Tiptap JSON.
 * Returns `null` when no conversion applies.
 */
export function normalizeImportedTiptapContent(content: unknown): TiptapJSON | null {
    if (!isTiptapDocument(content)) {
        return null;
    }
    const doc = content as TiptapJSON;
    if (!isPlainParagraphMarkdownCarrierDoc(doc)) {
        return null;
    }
    const md = docToJoinedPlainLines(doc);
    if (!plainTextLooksLikeMarkdownSource(md)) {
        return null;
    }
    return markdownToTiptap(md);
}
